import _ from 'lodash';

// Campos conocidos que NO son datos de análisis
const NON_ANALYSIS_FIELDS = new Set([
  'laboratoryId', 'samplingId', 'clientPdf', 'clientValue', 'client',
  'orchardPdf', 'orchard', 'orchardValue', 'confidencePorcentageOrchard',
  'confidencePercentageOrchard', 'quarterPdf', 'quarter', 'quarterValue',
  'confidencePercentageQuarter', 'varietyPdf', 'variety', 'varietyValue',
  'specie', 'specieValue', 'agroBusiness', 'agroBusinessValue',
  'cc', 'ccValue', 'season', 'processingStatus', 'analysisType',
  'analysisDate', 'rootStock', 'rootStockValue',
]);

// Extraer campos de análisis (soil1*, leaf*, etc.) de un objeto plano
const extractAnalysisFields = (flatItem) => {
  const analysisField = {};
  const analysisType = flatItem.analysisType || 'soilAnalysis';

  const fields = {};
  Object.keys(flatItem).forEach((key) => {
    if (!NON_ANALYSIS_FIELDS.has(key) && (key.startsWith('soil') || key.startsWith('leaf'))) {
      fields[key] = flatItem[key];
    }
  });

  if (Object.keys(fields).length > 0) {
    analysisField[analysisType] = fields;
  }

  return analysisField;
};

// Transformar la respuesta plana del backend en la estructura jerárquica
// que espera el frontend: document → orchards[] → cc[] → quarters[] → varieties[]
export const transformFlatDataToDocuments = (apiResponse) => {
  if (!apiResponse) return [];

  const firebaseUrls = apiResponse.firebaseStorageUrl || [];
  const documents = [];

  // Iterar las claves dinámicas del response (cada clave es un PDF procesado)
  Object.keys(apiResponse).forEach((pdfKey) => {
    if (pdfKey === 'firebaseStorageUrl') return;

    // La estructura puede ser:
    // response[pdfKey][pdfKey].input.data, response[pdfKey].input.data,
    // response[pdfKey].data, o response[pdfKey] (si es array directo)
    const pdfData = apiResponse[pdfKey];
    const inputData = pdfData?.[pdfKey]?.input?.data
      || pdfData?.input?.data
      || pdfData?.data
      || (Array.isArray(pdfData) ? pdfData : null);

    if (!inputData || !Array.isArray(inputData)) return;

    const pdfFirebaseUrls = pdfData?.[pdfKey]?.input?.firebaseUrls
      || pdfData?.input?.firebaseUrls
      || pdfData?.firebaseUrls
      || [];

    // Cada item es una fila independiente — usar item.url como clave única
    // en todos los niveles para evitar que items con mismo orchardValue/ccValue/quarterValue
    // se fusionen y compartan estado
    const orchardMap = {};

    inputData.forEach((item) => {
      const itemUrl = item.url || `item_${Math.random()}`;

      if (!orchardMap[itemUrl]) {
        orchardMap[itemUrl] = {
          orchardValue: item.orchardValue || '',
          orchard: item.orchard || '',
          orchardPdf: item.orchardPdf || '',
          agroBusinessValue: item.agroBusinessValue || '',
          agroBusiness: item.agroBusiness || '',
          confidencePercentageOrchard: item.confidencePercentageOrchard
            ?? item.confidencePorcentageOrchard
            ?? 0,
          firebaseStorageUrl: pdfFirebaseUrls.length > 0 ? pdfFirebaseUrls : firebaseUrls,
          ccMap: {},
        };
      }

      const ccKey = itemUrl;
      if (!orchardMap[itemUrl].ccMap[ccKey]) {
        orchardMap[itemUrl].ccMap[ccKey] = {
          ccValue: item.ccValue || '',
          cc: item.cc || '',
          specie: item.specie || '',
          specieValue: item.specieValue || '',
          quarterMap: {},
        };
      }

      const quarterKey = itemUrl;
      if (!orchardMap[itemUrl].ccMap[ccKey].quarterMap[quarterKey]) {
        orchardMap[itemUrl].ccMap[ccKey].quarterMap[quarterKey] = {
          quarterValue: item.quarterValue || '',
          quarter: item.quarter || '',
          quarterPdf: item.quarterPdf || '',
          confidencePercentageQuarter: item.confidencePercentageQuarter ?? 0,
          confidencePercentageOrchard: item.confidencePercentageOrchard
            ?? item.confidencePorcentageOrchard
            ?? 0,
          fileName: item.fileName || '',
          url: item.url || '',
          processingStatus: item.processingStatus || 'sin_asignar',
          varieties: [],
        };
      }

      // Agregar variedad con sus campos de análisis
      orchardMap[itemUrl].ccMap[ccKey].quarterMap[quarterKey].varieties.push({
        varietyValue: item.varietyValue || '',
        variety: item.variety || '',
        varietyPdf: item.varietyPdf || '',
        rootStockValue: item.rootStockValue || '',
        rootStock: item.rootStock || '',
        analysisField: extractAnalysisFields(item),
      });
    });

    // Convertir los maps anidados en arrays
    const orchards = Object.values(orchardMap).map((orch) => {
      const cc = Object.values(orch.ccMap).map((ccItem) => ({
        ccValue: ccItem.ccValue,
        cc: ccItem.cc,
        specie: ccItem.specie,
        specieValue: ccItem.specieValue,
        quarters: Object.values(ccItem.quarterMap),
      }));

      return {
        orchardValue: orch.orchardValue,
        orchard: orch.orchard,
        orchardPdf: orch.orchardPdf,
        agroBusinessValue: orch.agroBusinessValue,
        agroBusiness: orch.agroBusiness,
        confidencePercentageOrchard: orch.confidencePercentageOrchard,
        firebaseStorageUrl: orch.firebaseStorageUrl,
        cc,
      };
    });

    // Tomar metadatos del primer item para el documento
    const firstItem = inputData[0] || {};
    documents.push({
      documentId: pdfKey + '_' + Date.now(),
      clientPdf: firstItem.clientPdf || '',
      clientValue: firstItem.clientValue || '',
      client: firstItem.client || '',
      season: firstItem.season || '',
      analysisType: firstItem.analysisType || '',
      analysisDate: firstItem.analysisDate || '',
      processingStatus: firstItem.processingStatus || 'sin_asignar',
      fileName: firebaseUrls[0]?.fileName || pdfFirebaseUrls[0]?.fileName || pdfKey,
      orchards,
    });
  });

  return documents;
};

// Transformar respuesta del upload para el state
export const transformUploadResponse = (apiData) => {
  if (!apiData) return { documents: [], firebaseUrls: [] };

  return {
    documents: transformFlatDataToDocuments(apiData),
    firebaseUrls: apiData.firebaseStorageUrl || [],
  };
};

// Construir ID compuesto estable para filas de la tabla de huertos
export const buildRowId = (documentId, orchardIdx, ccIdx, quarterIdx) => {
  return `${documentId}__${orchardIdx}__${ccIdx}__${quarterIdx}`;
};

// Parsear ID compuesto en sus componentes
export const parseRowId = (rowId) => {
  const [documentId, orchardIdx, ccIdx, quarterIdx] = rowId.split('__');
  return {
    documentId,
    orchardIdx: parseInt(orchardIdx, 10),
    ccIdx: parseInt(ccIdx, 10),
    quarterIdx: parseInt(quarterIdx, 10),
  };
};

// Aplanar documentos para tabla AG-Grid del paso 2
// Cada fila = 1 registro del backend (document → orchard → cc → quarter)
export const flattenForOrchardTable = (documents) => {
  if (!documents || !documents.length) return [];

  const rows = [];

  documents.forEach((doc) => {
    if (!doc.orchards || !doc.orchards.length) {
      rows.push({
        rowId: buildRowId(doc.documentId, 0, 0, 0),
        documentId: doc.documentId,
        fileName: doc.fileName || 'Sin nombre',
        orchardPdf: '',
        orchardValue: '',
        orchard: '',
        agroBusinessValue: '',
        agroBusiness: '',
        confidencePercentageOrchard: 0,
        ccValue: '',
        cc: '',
        quarterValue: '',
        quarter: '',
        quarterPdf: '',
        variety: '',
        varietyValue: '',
        processingStatus: doc.processingStatus || 'sin_asignar',
        analysisType: doc.analysisType || '',
        _orchardIndex: 0,
        _ccIndex: 0,
        _quarterIndex: 0,
      });
      return;
    }

    doc.orchards.forEach((orch, orchIdx) => {
      if (!orch.cc || !orch.cc.length) {
        rows.push({
          rowId: buildRowId(doc.documentId, orchIdx, 0, 0),
          documentId: doc.documentId,
          fileName: doc.fileName || orch.firebaseStorageUrl?.[0]?.fileName || 'Sin nombre',
          orchardPdf: orch.orchardPdf || '',
          orchardValue: orch.orchardValue || '',
          orchard: orch.orchard || '',
          agroBusinessValue: orch.agroBusinessValue || '',
          agroBusiness: orch.agroBusiness || '',
          confidencePercentageOrchard: orch.confidencePercentageOrchard || 0,
          ccValue: '',
          cc: '',
          quarterValue: '',
          quarter: '',
          quarterPdf: '',
          variety: '',
          varietyValue: '',
          processingStatus: doc.processingStatus || 'sin_asignar',
          analysisType: doc.analysisType || '',
          _orchardIndex: orchIdx,
          _ccIndex: 0,
          _quarterIndex: 0,
        });
        return;
      }

      orch.cc.forEach((ccItem, ccIdx) => {
        if (!ccItem.quarters || !ccItem.quarters.length) {
          rows.push({
            rowId: buildRowId(doc.documentId, orchIdx, ccIdx, 0),
            documentId: doc.documentId,
            fileName: doc.fileName || orch.firebaseStorageUrl?.[0]?.fileName || 'Sin nombre',
            orchardPdf: orch.orchardPdf || '',
            orchardValue: orch.orchardValue || '',
            orchard: orch.orchard || '',
            agroBusinessValue: orch.agroBusinessValue || '',
            agroBusiness: orch.agroBusiness || '',
            confidencePercentageOrchard: orch.confidencePercentageOrchard || 0,
            ccValue: ccItem.ccValue || '',
            cc: ccItem.cc || '',
            quarterValue: '',
            quarter: '',
            quarterPdf: '',
            variety: '',
            varietyValue: '',
            processingStatus: doc.processingStatus || 'sin_asignar',
            analysisType: doc.analysisType || '',
            _orchardIndex: orchIdx,
            _ccIndex: ccIdx,
            _quarterIndex: 0,
          });
          return;
        }

        ccItem.quarters.forEach((q, qIdx) => {
          const firstVariety = q.varieties?.[0] || {};
          rows.push({
            rowId: buildRowId(doc.documentId, orchIdx, ccIdx, qIdx),
            documentId: doc.documentId,
            fileName: q.fileName || doc.fileName || orch.firebaseStorageUrl?.[0]?.fileName || 'Sin nombre',
            orchardPdf: orch.orchardPdf || '',
            orchardValue: orch.orchardValue || '',
            orchard: orch.orchard || '',
            agroBusinessValue: orch.agroBusinessValue || '',
            agroBusiness: orch.agroBusiness || '',
            confidencePercentageOrchard: q.confidencePercentageOrchard ?? orch.confidencePercentageOrchard ?? 0,
            ccValue: ccItem.ccValue || '',
            cc: ccItem.cc || '',
            quarterValue: q.quarterValue || '',
            quarter: q.quarter || '',
            quarterPdf: q.quarterPdf || '',
            variety: firstVariety.variety || '',
            varietyValue: firstVariety.varietyValue || '',
            url: q.url || '',
            processingStatus: q.processingStatus || doc.processingStatus || 'sin_asignar',
            analysisType: doc.analysisType || '',
            _orchardIndex: orchIdx,
            _ccIndex: ccIdx,
            _quarterIndex: qIdx,
          });
        });
      });
    });
  });

  return rows;
};

// Agrupar documentos por huerto para el paso 3 (accordion)
export const groupDocumentsByOrchard = (documents) => {
  if (!documents || !documents.length) return [];

  const orchardMap = {};

  documents.forEach((doc) => {
    if (!doc.orchards) return;

    doc.orchards.forEach((orchard) => {
      const key = orchard.orchardValue || `unassigned_${doc.documentId}`;

      if (!orchardMap[key]) {
        orchardMap[key] = {
          orchardValue: orchard.orchardValue,
          orchard: orchard.orchard,
          agroBusinessValue: orchard.agroBusinessValue,
          agroBusiness: orchard.agroBusiness,
          quarters: [],
          totalQuarters: 0,
          pendingQuarters: 0,
          validatedQuarters: 0,
        };
      }

      if (orchard.cc) {
        orchard.cc.forEach((ccItem) => {
          if (ccItem.quarters) {
            ccItem.quarters.forEach((quarter) => {
              orchardMap[key].quarters.push({
                documentId: doc.documentId,
                orchardValue: orchard.orchardValue,
                ccValue: ccItem.ccValue,
                cc: ccItem.cc,
                specie: ccItem.specie,
                specieValue: ccItem.specieValue,
                quarterValue: quarter.quarterValue,
                quarter: quarter.quarter,
                quarterPdf: quarter.quarterPdf,
                confidencePercentageQuarter: quarter.confidencePercentageQuarter || 0,
                varieties: quarter.varieties || [],
                status: quarter.quarterValue ? 'validado' : 'pendiente',
              });

              orchardMap[key].totalQuarters++;
              if (quarter.quarterValue) {
                orchardMap[key].validatedQuarters++;
              } else {
                orchardMap[key].pendingQuarters++;
              }
            });
          }
        });
      }
    });
  });

  return Object.values(orchardMap);
};

// Construir payload para guardar documentos al backend
export const buildSavePayload = (documents, username) => {
  return {
    input: {
      username: username,
      data: documents,
    },
  };
};

// Aplanar documentos de visualización (Pestaña 2) para tabla
export const flattenForVisualizationTable = (documents) => {
  if (!documents || !documents.length) return [];

  return documents.map((doc) => ({
    documentId: doc.documentId || doc._id,
    username: doc.username || '',
    analysisType: doc.analysisType || '',
    documentCount: doc.orchards?.length || 0,
    createdAt: doc.createdAt || '',
    processingStatus: doc.processingStatus || '',
    isProcessed: doc.isProcessed || false,
    _original: doc,
  }));
};

// Formatear tamaño de archivo
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};
